/**
 * Google Analytics 4 — ekavi.care
 *
 * Three rules:
 *
 * 1. **Production only.** In `vite dev` every function is a no-op.
 *
 * 2. **Consent Mode v2, region-aware.** EU/EEA/UK/CH visitors get
 *    cookieless, modeled pings (`analytics_storage='denied'`) until the
 *    consent banner returns 'granted'. Everyone else is granted by
 *    default. Ads are denied unconditionally — this site does not run
 *    advertising.
 *
 * 3. **No ID, no analytics.** If `PUBLIC_GA_MEASUREMENT_ID` is unset,
 *    `initGA()` returns immediately. Useful for preview deploys and
 *    forks.
 *
 * Events flow through a single `sendEvent` so the rules above can never
 * be bypassed by a typo at the call site.
 */

import { dev, browser } from '$app/environment';
import { env as publicEnv } from '$env/dynamic/public';

const GA_MEASUREMENT_ID = publicEnv.PUBLIC_GA_MEASUREMENT_ID;

/* ------------------------------------------------------------------ */
/*  Region detection                                                   */
/* ------------------------------------------------------------------ */

const GDPR_REGIONS = [
	'AT',
	'BE',
	'BG',
	'HR',
	'CY',
	'CZ',
	'DK',
	'EE',
	'FI',
	'FR',
	'DE',
	'GR',
	'HU',
	'IE',
	'IT',
	'LV',
	'LT',
	'LU',
	'MT',
	'NL',
	'PL',
	'PT',
	'RO',
	'SK',
	'SI',
	'ES',
	'SE',
	// EEA non-EU
	'IS',
	'LI',
	'NO',
	// UK + Switzerland mirror GDPR posture
	'GB',
	'CH'
];

function isGdprTimezone(): boolean {
	if (!browser) return false;
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
		return tz.startsWith('Europe/') || tz === 'Atlantic/Reykjavik' || tz === 'Atlantic/Faroe';
	} catch {
		return false;
	}
}

/* ------------------------------------------------------------------ */
/*  Consent persistence                                                */
/* ------------------------------------------------------------------ */

const CONSENT_STORAGE_KEY = 'ek_consent';
const CONSENT_SCHEMA_VERSION = 1;

type ConsentDecision = 'granted' | 'denied';

interface StoredConsent {
	v: number;
	analytics: ConsentDecision;
	at: number;
}

function readStoredConsent(): StoredConsent | null {
	if (!browser) return null;
	try {
		const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
		const obj = parsed as Record<string, unknown>;
		if (obj.v !== CONSENT_SCHEMA_VERSION) return null;
		if (obj.analytics !== 'granted' && obj.analytics !== 'denied') return null;
		if (typeof obj.at !== 'number' || !Number.isFinite(obj.at)) return null;
		return { v: obj.v, analytics: obj.analytics, at: obj.at };
	} catch {
		return null;
	}
}

export function needsConsentDecision(): boolean {
	if (!browser) return false;
	if (!GA_MEASUREMENT_ID) return false;
	if (!isGdprTimezone()) return false;
	return readStoredConsent() === null;
}

export function setConsent(next: ConsentDecision): void {
	if (!browser) return;
	if (next !== 'granted' && next !== 'denied') return;

	try {
		const payload: StoredConsent = {
			v: CONSENT_SCHEMA_VERSION,
			analytics: next,
			at: Date.now()
		};
		localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* private mode / quota — choice is still respected this session */
	}

	if (dev || !GA_MEASUREMENT_ID) return;

	try {
		if (typeof window.gtag === 'function') {
			window.gtag('consent', 'update', {
				analytics_storage: next,
				ad_storage: 'denied',
				ad_user_data: 'denied',
				ad_personalization: 'denied'
			});
		}
	} catch {
		/* analytics must never break the app */
	}

	sendEventRaw('consent_update', { decision: next });
}

function replayStoredConsent(): void {
	const stored = readStoredConsent();
	if (!stored) return;
	if (typeof window.gtag !== 'function') return;
	try {
		window.gtag('consent', 'update', {
			analytics_storage: stored.analytics,
			ad_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied'
		});
	} catch {
		/* never break the app */
	}
}

/* ------------------------------------------------------------------ */
/*  Initialisation                                                     */
/* ------------------------------------------------------------------ */

let initialized = false;

export function initGA(): void {
	if (!browser || dev || initialized) return;
	if (!GA_MEASUREMENT_ID) return;
	initialized = true;

	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag(...args: unknown[]) {
		(window.dataLayer as unknown[]).push(args);
	};

	// EU/UK/CH default: deny everything; modeled pings until consented.
	window.gtag('consent', 'default', {
		analytics_storage: 'denied',
		ad_storage: 'denied',
		ad_user_data: 'denied',
		ad_personalization: 'denied',
		wait_for_update: 30000,
		region: GDPR_REGIONS
	});
	// Everyone else: analytics granted, ads denied (we don't run ads).
	window.gtag('consent', 'default', {
		analytics_storage: 'granted',
		ad_storage: 'denied',
		ad_user_data: 'denied',
		ad_personalization: 'denied'
	});

	replayStoredConsent();

	const debug = new URLSearchParams(window.location.search).has('debug_ga');
	window.gtag('js', new Date());
	window.gtag('config', GA_MEASUREMENT_ID, {
		send_page_view: false,
		...(debug && { debug_mode: true })
	});

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
	document.head.appendChild(script);
}

/* ------------------------------------------------------------------ */
/*  UTM capture (first-touch, session-scoped)                          */
/* ------------------------------------------------------------------ */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const UTM_STORAGE_KEY = 'ek_utm';

type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

let cachedUtm: UtmParams | null = null;

export function captureUtmParams(): UtmParams {
	if (!browser) return {};
	if (cachedUtm) return cachedUtm;

	const params = new URLSearchParams(window.location.search);
	const fromUrl: UtmParams = {};
	let hasUrlUtm = false;
	for (const key of UTM_KEYS) {
		const val = params.get(key);
		if (val) {
			fromUrl[key] = val;
			hasUrlUtm = true;
		}
	}

	if (hasUrlUtm) {
		try {
			sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl));
		} catch {
			/* quota */
		}
		cachedUtm = fromUrl;
		return cachedUtm;
	}

	try {
		const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
		cachedUtm = stored ? (JSON.parse(stored) as UtmParams) : {};
	} catch {
		cachedUtm = {};
	}
	return cachedUtm;
}

function getUtmParams(): UtmParams {
	if (cachedUtm) return cachedUtm;
	return captureUtmParams();
}

/* ------------------------------------------------------------------ */
/*  Event dispatch                                                     */
/* ------------------------------------------------------------------ */

type EventParams = Record<string, unknown>;

function sendEventRaw(action: string, params?: EventParams): void {
	if (!browser || dev || !GA_MEASUREMENT_ID) return;
	try {
		if (typeof window.gtag === 'function') {
			const utm = getUtmParams();
			window.gtag('event', action, { ...utm, ...params });
		}
	} catch {
		/* analytics must never break the app */
	}
}

function sendEvent(action: string, params?: EventParams): void {
	sendEventRaw(action, params);
}

/* ------------------------------------------------------------------ */
/*  Public events                                                      */
/* ------------------------------------------------------------------ */

export function trackPageView(path: string, title: string): void {
	if (!browser) return;
	sendEvent('page_view', {
		page_title: title,
		page_location: window.location.href,
		page_path: path,
		page_referrer: document.referrer
	});
}

export function trackCtaClick(ctaText: string, location: string, destination: string): void {
	sendEvent('cta_click', {
		cta_text: ctaText,
		content_group: location,
		link_url: destination
	});
}

export function trackApplyOpen(source: string): void {
	sendEvent('apply_open', { content_group: source });
}

export function trackApplyClose(reason: 'dismiss' | 'backdrop' | 'escape'): void {
	sendEvent('apply_close', { interaction_type: reason });
}

export function trackApplySubmit(opts: { has_practice: boolean }): void {
	sendEvent('generate_lead', {
		currency: 'USD',
		value: 0,
		lead_source: 'design_partner_application',
		form_name: 'apply',
		has_practice: opts.has_practice
	});
}

export function trackApplyError(
	step: 'validation' | 'network' | 'server',
	errorMessage: string
): void {
	sendEvent('apply_error', {
		error_step: step,
		error_message: errorMessage.slice(0, 120)
	});
}

export function trackFormFieldInteraction(
	fieldName: string,
	action: 'focus' | 'blur_empty' | 'blur_filled'
): void {
	sendEvent('form_interaction', {
		form_name: 'apply',
		field_name: fieldName,
		interaction: action
	});
}

export function trackTrustPacketDownload(kind: 'verification' | 'baa'): void {
	sendEvent('trust_packet_download', { packet_kind: kind });
}

export function trackBlogRead(slug: string, seconds: number): void {
	sendEvent('blog_read', { article_slug: slug, seconds_elapsed: seconds });
}

export function trackMorningSentenceState(state: string): void {
	sendEvent('morning_sentence_state', { state });
}

/* ------------------------------------------------------------------ */
/*  Outbound links                                                     */
/* ------------------------------------------------------------------ */

export function enableOutboundTracking(): () => void {
	if (!browser) return () => {};

	function onDocClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;
		const anchor = target.closest('a');
		if (!anchor) return;
		const href = anchor.getAttribute('href') || '';
		if (!href || href.startsWith('#') || href.startsWith('/')) return;
		if (/^(mailto|tel|javascript):/i.test(href)) return;

		try {
			const url = new URL(href, window.location.origin);
			if (url.host === window.location.host) return;
			const label = (anchor.textContent || '').trim().slice(0, 60);
			const section = anchor.closest('section[id]');
			const location = section?.id ?? 'body';
			sendEvent('outbound_click', {
				link_text: label,
				link_url: url.href,
				content_group: location
			});
		} catch {
			/* malformed href */
		}
	}

	document.addEventListener('click', onDocClick, true);
	return () => document.removeEventListener('click', onDocClick, true);
}
